import { ConversationStatus } from '@provision/conversation-types';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import { Conversation, useGroupRules, UserGroupRules } from 'src/graphql';
import { useConversationContext } from 'src/graphql/entities/conversation';
import { useConversationDelete } from 'src/graphql/entities/conversation/use-conversation-delete';
import { EntityNames } from 'src/graphql/types';
import { renderDocumentAsMarkdown } from 'src/pages/Conversations/document';
import {
  buildConversationDocument,
  type ConversationMarkdownTimezoneStrategy,
} from 'src/pages/Conversations/markdown';
import { useConversationPdfExport } from 'src/pages/Conversations/pdf';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import {
  useConfirmationModal,
  useCopyToClipboard,
  useEntityFullUrl,
  useGetTosUrl,
  useNotifications,
} from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

interface ConversationAction {
  handler: () => void | Promise<void>;
  icon: string;
  iconLibrary?: IconLibrary;
  isAllowed?: boolean;
  isLoading?: boolean;
  label: string;
}

export const useConversationActions = ({
  conversation,
  handleAfterDelete,
  customViewId,
}: {
  conversation: Conversation;
  handleAfterDelete?: () => void;
  customViewId?: string | null;
}): Record<string, ConversationAction> => {
  const { tosUrl } = useGetTosUrl({
    partnerId: conversation?.partnerId,
    brandId: conversation?.address?.brandId,
  });
  const { confirm } = useConfirmationModal();
  const { deleteConversation } = useConversationDelete();
  const { showModal } = useModalMethods(Modals.MergeConversationModal);

  const { navigate } = useNavigateRoute();
  const { copyToClipboard, copyFriendlyLink } = useCopyToClipboard();
  const { copyToClipboard: copyMarkdownToClipboard } = useCopyToClipboard({
    successMessage: 'Conversation markdown copied.',
  });
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { getConversationContext, loading: conversationContextLoading } =
    useConversationContext();
  const { exportConversationAsPdf, isExporting: isExportingConversationAsPdf } =
    useConversationPdfExport();
  const { groupRules } = useGroupRules();
  const [isCopyingConversationAsMarkdown, setIsCopyingConversationAsMarkdown] =
    useState(false);
  const [isDownloadingConversationAsPdf, setIsDownloadingConversationAsPdf] = useState(false);

  const canDeleteConversation =
    groupRules[UserGroupRules.AccessToDeleteConversations] && !conversation.isDeleted;
  const canUpdateConversation =
    groupRules[UserGroupRules.AccessToUpdateConversation] && !conversation.isDeleted;
  const canShowActivityFeed = Boolean(
    groupRules[UserGroupRules.AccessToActivityFeedInConversations],
  );
  const canCopyConversationAsMarkdown = Boolean(
    groupRules[UserGroupRules.AccessToCopyConversationAsMarkdown],
  );
  const canMergeConversation =
    (conversation.conversationStatusId as ConversationStatus) !== ConversationStatus.Closed &&
    canUpdateConversation &&
    !conversation.isDeleted;

  const conversationUrl = useEntityFullUrl(
    conversation?.partnerId,
    EntityNames.Conversation,
    conversation.friendlyId,
  );

  const activityFeedEnabled = useGlobalStore.use.activityFeed();
  const toggleActivityFeed = useGlobalStore.use.toggleConversationsActivityFeed();
  const isConversationExportBusy =
    isCopyingConversationAsMarkdown ||
    isDownloadingConversationAsPdf ||
    conversationContextLoading ||
    isExportingConversationAsPdf;
  const isCopyConversationAsMarkdownLoading = isCopyingConversationAsMarkdown;
  const isDownloadConversationAsPdfLoading =
    isDownloadingConversationAsPdf || isExportingConversationAsPdf;

  const buildConversationExport = useCallback(
    async ({
      timezoneStrategy,
    }: {
      timezoneStrategy: ConversationMarkdownTimezoneStrategy;
    }) => {
      if (!conversation.conversationId) {
        throw new Error('Conversation ID is not available.');
      }

      const response = await getConversationContext({
        conversationId: conversation.conversationId,
        partnerId: conversation.partnerId,
      });
      const context = response?.data?.getConversationContext;

      if (!context) {
        throw new Error('Conversation context is not available.');
      }

      const document = buildConversationDocument({
        conversation,
        context,
        conversationUrl,
        policy: { timezoneStrategy },
      });
      const markdown = renderDocumentAsMarkdown(document);

      return { context, document, markdown };
    },
    [conversation, conversationUrl, getConversationContext],
  );

  const handleCopyConversationAsMarkdown = useCallback(async () => {
    if (isConversationExportBusy) {
      return;
    }

    setIsCopyingConversationAsMarkdown(true);

    try {
      const { markdown } = await buildConversationExport({
        timezoneStrategy: 'user_local_time',
      });

      if (!markdown.trim()) {
        throw new Error('Conversation markdown export is empty.');
      }

      await copyMarkdownToClipboard(markdown);
    } catch {
      showErrorAlert('Failed to copy conversation as markdown. Please try again.');
    } finally {
      setIsCopyingConversationAsMarkdown(false);
    }
  }, [
    buildConversationExport,
    isConversationExportBusy,
    copyMarkdownToClipboard,
    showErrorAlert,
  ]);

  const handleDownloadConversationAsPdf = useCallback(async () => {
    if (isConversationExportBusy) {
      return;
    }

    setIsDownloadingConversationAsPdf(true);

    try {
      const { context, document } = await buildConversationExport({
        timezoneStrategy: 'user_local_time',
      });

      await exportConversationAsPdf({
        context,
        conversation: {
          friendlyId: conversation.friendlyId,
          subject: conversation.subject,
        },
        document,
      });

      showSuccessAlert('Conversation PDF download started.');
    } catch {
      showErrorAlert('Failed to download conversation as PDF. Please try again.');
    } finally {
      setIsDownloadingConversationAsPdf(false);
    }
  }, [
    buildConversationExport,
    conversation,
    exportConversationAsPdf,
    isConversationExportBusy,
    showErrorAlert,
    showSuccessAlert,
  ]);

  return useMemo(
    () => ({
      copyConversationId: {
        label: 'Copy Conversation ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: () => copyToClipboard(conversation.friendlyId),
      },
      copyFriendlyIdAsLink: {
        label: 'Copy Friendly ID as link',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: () => copyFriendlyLink(conversation.friendlyId, conversationUrl),
      },
      copyUrl: {
        label: 'Copy Conversation URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!conversationUrl,
        handler: () => copyToClipboard(conversationUrl),
      },
      copyConversationAsMarkdown: {
        label: isCopyConversationAsMarkdownLoading
          ? 'Copying Conversation as Markdown...'
          : 'Copy Conversation as Markdown',
        icon: 'file-text',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canCopyConversationAsMarkdown,
        isLoading: isCopyConversationAsMarkdownLoading,
        handler: handleCopyConversationAsMarkdown,
      },
      downloadConversationAsPdf: {
        label: isDownloadConversationAsPdfLoading
          ? 'Downloading Conversation as PDF...'
          : 'Download Conversation as PDF',
        icon: 'file-text',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canCopyConversationAsMarkdown,
        isLoading: isDownloadConversationAsPdfLoading,
        handler: handleDownloadConversationAsPdf,
      },
      copyUuid: {
        label: 'Copy UUID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        handler: () => copyToClipboard(conversation.conversationId),
      },
      copyTOS: {
        label: 'Copy Terms of Service URL',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!tosUrl,
        handler: async () => {
          await copyToClipboard(tosUrl);
        },
      },
      showActivityFeed: {
        label: `${activityFeedEnabled.conversations ? 'Hide' : 'Show'} Activity`,
        icon: 'calendar-clock',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canShowActivityFeed,
        handler: () => toggleActivityFeed(),
      },
      mergeConversation: {
        label: 'Merge conversation',
        icon: 'merge',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canMergeConversation,
        handler: () => {
          showModal();
        },
      },
      deleteConversation: {
        label: 'Delete conversation',
        icon: 'trash-2',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: canDeleteConversation && canUpdateConversation,
        handler: async () => {
          if (
            await confirm({
              body: 'Please confirm you want to delete the Conversation.',
              variant: 'delete',
            })
          ) {
            await deleteConversation(conversation.conversationId);
            if (handleAfterDelete) {
              handleAfterDelete();
            } else if (customViewId) {
              navigate(portalPaths.entity.selected('conversation', customViewId));
            } else {
              navigate(
                portalPaths.entity.all({
                  entityName: 'conversations',
                }),
              );
            }
          }
        },
      },
    }),
    [
      conversationUrl,
      tosUrl,
      activityFeedEnabled.conversations,
      isCopyConversationAsMarkdownLoading,
      isDownloadConversationAsPdfLoading,
      canCopyConversationAsMarkdown,
      canShowActivityFeed,
      canMergeConversation,
      canDeleteConversation,
      canUpdateConversation,
      copyToClipboard,
      handleCopyConversationAsMarkdown,
      handleDownloadConversationAsPdf,
      conversation.friendlyId,
      conversation.conversationId,
      copyFriendlyLink,
      toggleActivityFeed,
      showModal,
      confirm,
      deleteConversation,
      handleAfterDelete,
      customViewId,
      navigate,
    ],
  );
};
